import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { zynity_api } from '../../../../../config/external-routes';
import { OrgStatementTinyMceField } from '../../../../admin/organizations/details/OrgStatementTinyMceField';
import { DocumentLibraryPickerItem, DocumentLibraryPickerModal } from '../../../../ui/tools/DocumentLibraryPickerModal';
import {
    fetchLibraryContextItems,
    fetchLibraryContextItemsForMany,
    syncLibraryContextItems,
    registerLinkInLibrary,
    registerUploadInLibrary,
    STRATEGY_ITEM_CONTEXT_TYPE,
    STRATEGY_COMMENT_CONTEXT_TYPE,
} from './libraryContext';

type Comment = {
    id: number;
    comment: string;
    idAuthor: number;
    dateCreated: string | null;
    idStrategiesComments: number | null;
};

type Link = { id: number; links: string };
type Upload = { id: number; fileName: string };

type EntityParam = 'detail_id' | 'next_step_id' | 'milestone_id';

const COMMENT_FIELD: Record<EntityParam, string> = {
    detail_id: 'idStrategyDetail',
    next_step_id: 'idStrategyNextStep',
    milestone_id: 'idStrategyMilestone',
};

type Props = {
    organizationId: string;
    teamId: string;
    initiativeId: string;
    entityParam: EntityParam;
    entityId: number;
    allowEdit: boolean;
    currentUserId?: number;
};

/** One reusable "Library" section for a comment composer/editor — pick/remove
 * existing Document Library docs, scoped to whatever context the caller is
 * managing (a not-yet-posted composer's local state, or a real comment's own
 * `strategy_comment` context once it exists). */
const CommentLibraryField = ({
    items,
    onChoose,
    onRemove,
    show,
    setShow,
    disabled,
}: {
    items: DocumentLibraryPickerItem[];
    onChoose: (item: DocumentLibraryPickerItem) => void;
    onRemove: (itemId: number) => void;
    show: boolean;
    setShow: (show: boolean) => void;
    disabled?: boolean;
}) => (
    <div className="mb-2">
        <div className="ws-v1-section-label small text-muted mb-1">Library</div>
        {items.length > 0 && (
            <ul className="ws-v1-detail-library-list small">
                {items.map((item) => (
                    <li key={item.id} className="d-flex justify-content-between align-items-center">
                        <span>{item.title}</span>
                        <Button size="sm" variant="outline-danger" disabled={disabled} onClick={() => onRemove(item.id)}>
                            Remove
                        </Button>
                    </li>
                ))}
            </ul>
        )}
        <Button size="sm" className="btn-teal" disabled={disabled} onClick={() => setShow(true)}>
            Choose from Library
        </Button>
        <DocumentLibraryPickerModal
            show={show}
            onHide={() => setShow(false)}
            onSelectItem={(item) => {
                if (!items.some((existing) => existing.id === item.id)) {
                    onChoose(item);
                }
                setShow(false);
            }}
            selectedItemIds={items.map((item) => item.id)}
        />
    </div>
);

/**
 * Comments + Links/Uploads panel (§4d of the migration plan) — shared across
 * Details items/subitems/topics and Milestone/Next-Step rows. Backend note: the
 * actual entity-target column names differ per table on the live schema (confirmed
 * 2026-08-15 against a real dev-DB clone of production) — StrategyLinks/StrategyUpload
 * each have their own distinct typo'd milestone column, see StrategyLink/StrategyUpload's
 * docblocks — the frontend only ever sends the clean `detail_id`/`next_step_id`/
 * `milestone_id` param names below and via COMMENT_FIELD; the backend maps those to
 * the real (occasionally misspelled) column per table.
 * Comments support one level of reply threading via idStrategiesComments (matching
 * the live `ws-v1-detail-comment--reply` modifier confirmed in Yii's source — replies
 * are visually indented, not nested further; a reply can't itself be replied to).
 * Library (Phase 7, §10) uses the generic `strategy_item` document-library context,
 * scoped to the parent row (`entityId`) — matching the "Edit Item"/"New Details Item"
 * modal's own Library section confirmed live. Extended 2026-08-27 (closing a
 * deliberate Phase 7 scope reduction): every individual Comment now also gets its
 * own separate `strategy_comment` context, matching Yii's
 * `buildStrategyCommentDocumentContext()` exactly — a comment's Library picks are
 * independent of the parent row's and of every other comment's. Also extended with
 * shadow-indexing (matches `DocumentLibraryBridge::syncContext()`): every raw link/
 * upload added to the parent-level Links/Uploads sections below also becomes a
 * reusable Document Library item automatically.
 */
export const CommentsAndAttachments = ({
    organizationId,
    teamId,
    initiativeId,
    entityParam,
    entityId,
    allowEdit,
    currentUserId,
}: Props) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [links, setLinks] = useState<Link[]>([]);
    const [uploads, setUploads] = useState<Upload[]>([]);
    const [libraryItems, setLibraryItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [showLibraryPicker, setShowLibraryPicker] = useState(false);
    const [syncingLibrary, setSyncingLibrary] = useState(false);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [newLinkUrl, setNewLinkUrl] = useState('');
    const [posting, setPosting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
    const [editingCommentText, setEditingCommentText] = useState('');
    const [replyingToId, setReplyingToId] = useState<number | null>(null);
    const [replyText, setReplyText] = useState('');

    // Matches Yii's `_comments.php` (teal-circle initials + first name + date before
    // every comment/reply body) — resolved client-side the same way Authors/Category
    // already are elsewhere in Strategies, from the team's own member list already in
    // the store, rather than a backend change.
    const organizationTeam = useSelector((state: any) => state.organization_team);
    const teamMembers: any[] = useMemo(() => organizationTeam?.data?.members ?? [], [organizationTeam?.data?.members]);
    const memberId = (member: any) => (member?.People_id !== undefined && member?.People_id !== null ? member.People_id : member?.id);
    const getAuthor = (authorId: number) => teamMembers.find((m: any) => Number(memberId(m)) === Number(authorId));
    const getAuthorInitials = (authorId: number) => {
        const author = getAuthor(authorId);
        if (!author) {
            return '--';
        }
        if (!author.FirstName) {
            return (author.EmailAddress ?? '').substring(0, 2).toUpperCase();
        }
        return `${author.FirstName.substring(0, 1)}${(author.LastName ?? '').substring(0, 1)}`.toUpperCase();
    };
    const getAuthorFirstName = (authorId: number) => {
        const author = getAuthor(authorId);
        return author?.FirstName || author?.KnownAs || author?.EmailAddress || 'Unknown';
    };
    const formatCommentDate = (dateCreated: string | null) => {
        if (!dateCreated) {
            return '';
        }
        return new Date(dateCreated).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    // Per-comment Library (see class docblock) — each comment id maps to its own
    // already-synced list; the "new"/"reply"/"editing" buckets hold picks made in a
    // composer that hasn't been saved to a real comment id yet.
    const [commentLibraryItems, setCommentLibraryItems] = useState<Record<number, DocumentLibraryPickerItem[]>>({});
    const [newCommentLibraryItems, setNewCommentLibraryItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [showNewCommentLibraryPicker, setShowNewCommentLibraryPicker] = useState(false);
    const [replyLibraryItems, setReplyLibraryItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [showReplyLibraryPicker, setShowReplyLibraryPicker] = useState(false);
    const [editingCommentLibraryItems, setEditingCommentLibraryItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [showEditCommentLibraryPicker, setShowEditCommentLibraryPicker] = useState(false);
    const [syncingCommentLibrary, setSyncingCommentLibrary] = useState(false);

    const commentsUrl = zynity_api.organization_team_strategies_comments
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const commentUrl = (commentId: number) =>
        zynity_api.organization_team_strategies_comment_item
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId)
            .replace('{initiative_id}', initiativeId)
            .replace('{comment_id}', String(commentId));

    const attachmentsUrl = zynity_api.organization_team_strategies_attachments
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const linksUrl = zynity_api.organization_team_strategies_attachment_links
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const linkItemUrl = (linkId: number) =>
        zynity_api.organization_team_strategies_attachment_link_item
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId)
            .replace('{initiative_id}', initiativeId)
            .replace('{link_id}', String(linkId));

    const uploadsUrl = zynity_api.organization_team_strategies_attachment_uploads
        .replace('{organization_id}', organizationId)
        .replace('{team_id}', teamId)
        .replace('{initiative_id}', initiativeId);

    const uploadItemUrl = (uploadId: number) =>
        zynity_api.organization_team_strategies_attachment_upload_item
            .replace('{organization_id}', organizationId)
            .replace('{team_id}', teamId)
            .replace('{initiative_id}', initiativeId)
            .replace('{upload_id}', String(uploadId));

    const fetchAll = () => {
        setLoading(true);
        Promise.all([
            axios.get(commentsUrl, { params: { [entityParam]: entityId } }),
            axios.get(attachmentsUrl, { params: { [entityParam]: entityId } }),
            fetchLibraryContextItems(organizationId, teamId, STRATEGY_ITEM_CONTEXT_TYPE, entityId),
        ])
            .then(async ([commentsResponse, attachmentsResponse, libraryDocs]) => {
                const fetchedComments: Comment[] = commentsResponse.data.comments ?? [];
                setComments(fetchedComments);
                setLinks(attachmentsResponse.data.links ?? []);
                setUploads(attachmentsResponse.data.uploads ?? []);
                setLibraryItems(libraryDocs);

                try {
                    const perComment = await fetchLibraryContextItemsForMany(
                        organizationId,
                        teamId,
                        STRATEGY_COMMENT_CONTEXT_TYPE,
                        fetchedComments.map((comment) => comment.id)
                    );
                    setCommentLibraryItems(perComment);
                } catch (error) {
                    setCommentLibraryItems({});
                }
            })
            .catch(() => {
                setComments([]);
                setLinks([]);
                setUploads([]);
                setLibraryItems([]);
                setCommentLibraryItems({});
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityParam, entityId]);

    const syncLibrary = async (nextItems: DocumentLibraryPickerItem[]) => {
        setSyncingLibrary(true);
        try {
            await syncLibraryContextItems(
                organizationId,
                teamId,
                STRATEGY_ITEM_CONTEXT_TYPE,
                entityId,
                `Strategies / ${entityParam}: ${entityId}`,
                nextItems.map((item) => item.id)
            );
            setLibraryItems(nextItems);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'The Library selection could not be updated.' });
        } finally {
            setSyncingLibrary(false);
        }
    };

    const handleChooseLibraryItem = (item: DocumentLibraryPickerItem) => {
        if (libraryItems.some((existing) => existing.id === item.id)) {
            setShowLibraryPicker(false);
            return;
        }
        syncLibrary([...libraryItems, item]);
        setShowLibraryPicker(false);
    };

    const removeLibraryItem = (itemId: number) => {
        syncLibrary(libraryItems.filter((item) => item.id !== itemId));
    };

    // Shadow-indexing (matches Yii's DocumentLibraryBridge::syncContext(), see class
    // docblock) — every raw link/upload added to the parent-level Links/Uploads
    // sections below also becomes a reusable Document Library item. Best-effort: a
    // failure here doesn't roll back the link/upload itself, which already saved.
    const mergeIntoLibrary = async (newItem: DocumentLibraryPickerItem) => {
        try {
            const nextIds = [...libraryItems.map((item) => item.id), newItem.id];
            await syncLibraryContextItems(
                organizationId,
                teamId,
                STRATEGY_ITEM_CONTEXT_TYPE,
                entityId,
                `Strategies / ${entityParam}: ${entityId}`,
                nextIds
            );
        } catch (error) {
            // Best-effort — see comment above.
        }
    };

    // Syncs one comment's own Library picks (context_type: 'strategy_comment') —
    // used right after a create/edit/reply POST/PUT succeeds and the real comment id
    // is known.
    const syncCommentLibrary = async (commentId: number, items: DocumentLibraryPickerItem[]) => {
        try {
            await syncLibraryContextItems(
                organizationId,
                teamId,
                STRATEGY_COMMENT_CONTEXT_TYPE,
                commentId,
                `Strategies / Comment: ${commentId}`,
                items.map((item) => item.id)
            );
        } catch (error) {
            // Best-effort — the comment itself already saved successfully.
        }
    };

    const handlePostComment = async () => {
        if (newComment.trim() === '') {
            return;
        }
        setPosting(true);
        try {
            const response = await axios.post(commentsUrl, {
                comment: newComment.trim(),
                [COMMENT_FIELD[entityParam]]: entityId,
            });
            const newCommentId = response.data?.comment?.id;
            if (newCommentId && newCommentLibraryItems.length > 0) {
                await syncCommentLibrary(newCommentId, newCommentLibraryItems);
            }
            setNewComment('');
            setNewCommentLibraryItems([]);
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This comment could not be posted.' });
        } finally {
            setPosting(false);
        }
    };

    const startEditComment = (comment: Comment) => {
        setEditingCommentId(comment.id);
        setEditingCommentText(comment.comment);
        setEditingCommentLibraryItems(commentLibraryItems[comment.id] ?? []);
    };

    const saveEditComment = async () => {
        if (editingCommentId === null || editingCommentText.trim() === '') {
            return;
        }
        try {
            await axios.put(commentUrl(editingCommentId), { comment: editingCommentText.trim() });
            await syncCommentLibrary(editingCommentId, editingCommentLibraryItems);
            setEditingCommentId(null);
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This comment could not be updated.' });
        }
    };

    const handlePostReply = async (parentId: number) => {
        if (replyText.trim() === '') {
            return;
        }
        setPosting(true);
        try {
            const response = await axios.post(commentsUrl, {
                comment: replyText.trim(),
                idStrategiesComments: parentId,
                [COMMENT_FIELD[entityParam]]: entityId,
            });
            const newReplyId = response.data?.comment?.id;
            if (newReplyId && replyLibraryItems.length > 0) {
                await syncCommentLibrary(newReplyId, replyLibraryItems);
            }
            setReplyText('');
            setReplyLibraryItems([]);
            setReplyingToId(null);
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This reply could not be posted.' });
        } finally {
            setPosting(false);
        }
    };

    const deleteComment = async (comment: Comment) => {
        const result = await Swal.fire({
            title: 'Delete this comment?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(commentUrl(comment.id));
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This comment could not be deleted.' });
        }
    };

    const handleAddLink = async () => {
        if (newLinkUrl.trim() === '') {
            return;
        }
        setPosting(true);
        try {
            const url = newLinkUrl.trim();
            await axios.post(linksUrl, { url, [entityParam]: entityId });
            try {
                const libraryItem = await registerLinkInLibrary(organizationId, teamId, url);
                await mergeIntoLibrary(libraryItem);
            } catch (error) {
                // Best-effort — see mergeIntoLibrary's comment.
            }
            setNewLinkUrl('');
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This link could not be added.' });
        } finally {
            setPosting(false);
        }
    };

    const removeLink = async (link: Link) => {
        try {
            await axios.delete(linkItemUrl(link.id));
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This link could not be removed.' });
        }
    };

    const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file, file.name);
            // displayFileName() below reconstructs the original name by dropping the
            // first 3 underscore-separated segments — the upload endpoint's default
            // Storage::put() filename has no underscores at all, so without an explicit
            // storage_name every uploaded file showed its random storage token instead
            // of its real name. storage_name is stored as-is (Storage::putFileAs), so
            // any underscores already in the original filename are preserved correctly.
            formData.append('storage_name', `${Date.now()}_${entityId}_${Math.random().toString(36).slice(2, 8)}_${file.name}`);
            const tempResponse = await axios.post(zynity_api.global_upload_temp_files, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            await axios.post(uploadsUrl, {
                file_name: tempResponse.data.file_name,
                [entityParam]: entityId,
            });
            try {
                const libraryItem = await registerUploadInLibrary(organizationId, teamId, file, file.name);
                await mergeIntoLibrary(libraryItem);
            } catch (error) {
                // Best-effort — see mergeIntoLibrary's comment.
            }
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Upload failed', text: 'This file could not be uploaded.' });
        } finally {
            setUploading(false);
        }
    };

    const removeUpload = async (upload: Upload) => {
        try {
            await axios.delete(uploadItemUrl(upload.id));
            fetchAll();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This file could not be removed.' });
        }
    };

    const displayFileName = (fileName: string) => {
        const parts = fileName.split('_');
        return parts.length > 3 ? parts.slice(3).join('_') : fileName;
    };

    if (loading) {
        return <p className="text-muted small">Loading...</p>;
    }

    const topLevelComments = comments.filter((comment) => !comment.idStrategiesComments);
    const repliesFor = (parentId: number) => comments.filter((comment) => comment.idStrategiesComments === parentId);

    const renderCommentRow = (comment: Comment, isReply: boolean) => (
        <div
            key={comment.id}
            className={`border rounded p-2${isReply ? ' ws-v1-detail-comment--reply ms-4 mt-2' : ''}`}
        >
            {editingCommentId === comment.id ? (
                <div>
                    <OrgStatementTinyMceField
                        id={`edit-comment-${comment.id}`}
                        name="editingComment"
                        height={120}
                        value={editingCommentText}
                        onChange={(_name, value) => setEditingCommentText(value)}
                    />
                    <CommentLibraryField
                        items={editingCommentLibraryItems}
                        onChoose={(item) => setEditingCommentLibraryItems((current) => [...current, item])}
                        onRemove={(itemId) =>
                            setEditingCommentLibraryItems((current) => current.filter((item) => item.id !== itemId))
                        }
                        show={showEditCommentLibraryPicker}
                        setShow={setShowEditCommentLibraryPicker}
                        disabled={syncingCommentLibrary}
                    />
                    <div className="d-flex gap-2 mt-2">
                        <Button size="sm" className="btn-teal" onClick={saveEditComment}>
                            Save
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setEditingCommentId(null)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="d-flex gap-2">
                    <span
                        className="strategies-comment-avatar"
                        title={getAuthorFirstName(comment.idAuthor)}
                    >
                        {getAuthorInitials(comment.idAuthor)}
                    </span>
                    <div className="flex-grow-1 d-flex justify-content-between align-items-start">
                    <div>
                        <div className="small">
                            <span className="strategies-comment-author-name">{getAuthorFirstName(comment.idAuthor)}</span>{' '}
                            <span className="strategies-comment-date">{formatCommentDate(comment.dateCreated)}</span>
                        </div>
                        <div className="small" dangerouslySetInnerHTML={{ __html: comment.comment }} />
                        {(commentLibraryItems[comment.id]?.length ?? 0) > 0 && (
                            <div className="small text-muted mt-1">
                                Library: {commentLibraryItems[comment.id].map((item) => item.title).join(', ')}
                            </div>
                        )}
                    </div>
                    <div className="d-flex gap-1">
                        {!isReply && allowEdit && (
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                    setReplyingToId(comment.id);
                                    setReplyText('');
                                    setReplyLibraryItems([]);
                                }}
                            >
                                Reply
                            </Button>
                        )}
                        {currentUserId === comment.idAuthor && (
                            <Button size="sm" variant="outline-secondary" onClick={() => startEditComment(comment)}>
                                Edit
                            </Button>
                        )}
                        {allowEdit && (
                            <Button size="sm" variant="outline-danger" onClick={() => deleteComment(comment)}>
                                Delete
                            </Button>
                        )}
                    </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="strategies-comments-and-attachments mt-2">
            <div className="mb-3">
                <div className="ws-v1-section-label small text-muted mb-1">Comments</div>
                {comments.length === 0 ? (
                    <p className="text-muted small mb-2">No comments yet.</p>
                ) : (
                    <div className="d-flex flex-column gap-2 mb-2">
                        {topLevelComments.map((comment) => (
                            <div key={comment.id}>
                                {renderCommentRow(comment, false)}
                                {repliesFor(comment.id).map((reply) => renderCommentRow(reply, true))}
                                {replyingToId === comment.id && (
                                    <div className="ms-4 mt-2">
                                        <OrgStatementTinyMceField
                                            id={`reply-comment-${comment.id}`}
                                            name="replyText"
                                            height={100}
                                            value={replyText}
                                            onChange={(_name, value) => setReplyText(value)}
                                        />
                                        <CommentLibraryField
                                            items={replyLibraryItems}
                                            onChoose={(item) => setReplyLibraryItems((current) => [...current, item])}
                                            onRemove={(itemId) =>
                                                setReplyLibraryItems((current) => current.filter((entry) => entry.id !== itemId))
                                            }
                                            show={showReplyLibraryPicker}
                                            setShow={setShowReplyLibraryPicker}
                                        />
                                        <div className="d-flex gap-2 mt-2">
                                            <Button
                                                size="sm"
                                                className="btn-teal"
                                                disabled={posting || replyText.trim() === ''}
                                                onClick={() => handlePostReply(comment.id)}
                                            >
                                                Post Reply
                                            </Button>
                                            <Button size="sm" variant="outline-secondary" onClick={() => setReplyingToId(null)}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <OrgStatementTinyMceField
                    id="new-comment"
                    name="newComment"
                    height={120}
                    value={newComment}
                    onChange={(_name, value) => setNewComment(value)}
                />
                <CommentLibraryField
                    items={newCommentLibraryItems}
                    onChoose={(item) => setNewCommentLibraryItems((current) => [...current, item])}
                    onRemove={(itemId) => setNewCommentLibraryItems((current) => current.filter((item) => item.id !== itemId))}
                    show={showNewCommentLibraryPicker}
                    setShow={setShowNewCommentLibraryPicker}
                />
                <div className="mt-2">
                    <Button size="sm" className="btn-teal" disabled={posting || newComment.trim() === ''} onClick={handlePostComment}>
                        Post
                    </Button>
                </div>
            </div>

            <div className="mb-3" data-testid="parent-library-section">
                <div className="ws-v1-section-label small text-muted mb-1">Library</div>
                <div className="small text-muted mb-2">Select existing library-only documents for this record.</div>
                {libraryItems.length > 0 && (
                    <ul className="ws-v1-detail-library-list small">
                        {libraryItems.map((item) => (
                            <li key={item.id} className="d-flex justify-content-between align-items-center">
                                <span>{item.title}</span>
                                {allowEdit && (
                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        disabled={syncingLibrary}
                                        onClick={() => removeLibraryItem(item.id)}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {libraryItems.length === 0 && <div className="text-muted small mb-2">No library-only documents selected.</div>}
                {allowEdit && (
                    <Button size="sm" className="btn-teal" disabled={syncingLibrary} onClick={() => setShowLibraryPicker(true)}>
                        Choose from Library
                    </Button>
                )}
                <DocumentLibraryPickerModal
                    show={showLibraryPicker}
                    onHide={() => setShowLibraryPicker(false)}
                    onSelectItem={handleChooseLibraryItem}
                    selectedItemIds={libraryItems.map((item) => item.id)}
                />
            </div>

            <div className="mb-3">
                <div className="ws-v1-section-label small text-muted mb-1">Links</div>
                {links.length > 0 && (
                    <ul className="ws-v1-detail-links-list small">
                        {links.map((link) => (
                            <li key={link.id} className="d-flex justify-content-between align-items-center">
                                <a href={link.links} target="_blank" rel="noopener noreferrer">
                                    {link.links}
                                </a>
                                {allowEdit && (
                                    <Button size="sm" variant="outline-danger" onClick={() => removeLink(link)}>
                                        Remove
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {allowEdit && (
                    <div className="d-flex gap-2">
                        <Form.Control
                            size="sm"
                            placeholder="https://..."
                            value={newLinkUrl}
                            onChange={(e) => setNewLinkUrl(e.target.value)}
                        />
                        <Button size="sm" className="btn-teal" disabled={posting || newLinkUrl.trim() === ''} onClick={handleAddLink}>
                            Add Link
                        </Button>
                    </div>
                )}
            </div>

            <div>
                <div className="ws-v1-section-label small text-muted mb-1">Uploads</div>
                {uploads.length > 0 && (
                    <ul className="ws-v1-detail-uploads-list small">
                        {uploads.map((upload) => (
                            <li key={upload.id} className="d-flex justify-content-between align-items-center">
                                <a href={zynity_api.download_file + upload.fileName} target="_blank" rel="noopener noreferrer">
                                    {displayFileName(upload.fileName)}
                                </a>
                                {allowEdit && (
                                    <Button size="sm" variant="outline-danger" onClick={() => removeUpload(upload)}>
                                        Remove
                                    </Button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                {allowEdit && (
                    <Form.Control size="sm" type="file" disabled={uploading} onChange={handleUploadFile} />
                )}
            </div>
        </div>
    );
};
